/**
 * v0.23.1 (ADR-123; B-N1, B-L3) — việc thuần của hộp "Nhập mã của bạn".
 *
 * Việc chạm tới một CHỦ TIỆM (đặt mã hộ, đăng xuất máy, cho nghỉ / đi làm lại, đăng xuất "các máy khác" của chính mình) máy chủ trả
 * 400 `canMa` kèm câu đúng việc đang làm. Giao diện không đoán trước việc nào cần mã (người thường không bao giờ bị hỏi): gọi
 * `goi(undefined)`, gặp `canMa` mới mở hộp, gõ mã xong gọi lại `goi(ma)`. Mã là của NGƯỜI ĐANG LÀM, không bao giờ của người bị chạm.
 * Hộp vẽ ở `HoiMaCuaToi.tsx` (có React).
 */
import { ApiFailure } from './api.ts'
import { T } from './text/vi.ts'

export type KetQuaMa<R> = { ket: 'xong'; gia: R } | { ket: 'thoi' }

/**
 * G-N1: Enter trong ô mã = "Xác nhận" — nhưng GIỮ phím (repeat) hoặc đang kiểm thì không gửi thêm lần nữa (mỗi lần gửi là một lượt thử mã).
 */
export function phimXacNhan(
  e: { key: string; repeat: boolean },
  ma: string,
  dangKiem: boolean,
): boolean {
  return e.key === 'Enter' && !e.repeat && !dangKiem && ma.trim() !== ''
}

/** Nội dung hộp: `cau` = câu của máy chủ (giữ nguyên suốt các lần thử), `loi` = "Mã đăng nhập chưa đúng." / đang khoá — hiện ngay dưới ô. */
export interface NoiDungHoiMa {
  cau: string
  loi: string | null
}

/**
 * Chạy `goi` có hỏi mã. `hoiMa` trả mã người dùng gõ, hoặc `null` khi bấm "Thôi". Mã sai (máy chủ lại trả `canMa`) thì hộp hỏi LẠI,
 * giữ câu gốc và hiện câu lỗi dưới ô — không đóng hộp, không đẩy lỗi lên đầu trang. Lỗi khác `canMa` ném nguyên.
 */
export async function chayCoMa<R>(
  goi: (ma: string | undefined) => Promise<R>,
  hoiMa: (x: NoiDungHoiMa) => Promise<string | null>,
  /**
   * G-N1: lỗi KHÁC `canMa` xảy ra SAU khi đã gõ mã (mất mạng, 404, 429…): có hàm này thì hộp vẫn mở và hiện câu ngay dưới ô (người dùng
   * thử lại hoặc bấm "Thôi") thay vì ném ra ngoài — tờ đang mở che thanh lỗi ở đầu trang. Không truyền = ném như trước.
   */
  loiTrongHop?: (e: unknown) => string,
): Promise<KetQuaMa<R>> {
  let cau: string
  try {
    return { ket: 'xong', gia: await goi(undefined) }
  } catch (e) {
    if (!(e instanceof ApiFailure) || !e.canMa) throw e
    cau = e.messageVi
  }
  let loi: string | null = null
  for (;;) {
    const ma = await hoiMa({ cau, loi })
    if (ma === null) return { ket: 'thoi' }
    try {
      return { ket: 'xong', gia: await goi(ma) }
    } catch (e) {
      if (e instanceof ApiFailure && e.canMa) loi = e.messageVi
      else if (loiTrongHop !== undefined) loi = loiTrongHop(e)
      else throw e
    }
  }
}

/** Câu hiện dưới ô mã khi lỗi không phải "mã chưa đúng": câu tiếng Việt của máy chủ, hoặc câu chung. */
export function cauLoiTrongHop(e: unknown): string {
  return e instanceof ApiFailure ? e.messageVi : T.serverFailed
}
